
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { alpha, useTheme } from '@mui/material/styles';
import { Container, Grid, Card, CardContent, LinearProgress, Button, Chip, Alert, CircularProgress } from '@mui/material';
import { AutorenewRounded, Storage, Token, Upgrade } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import anthropicImg from '../../assets/images/anthropic.png';
import bedrockImg from '../../assets/images/bedrock.png';
import openAiImg from '../../assets/images/openai.svg';
import { TabPanel, a11yProps } from '../../components/tabbar/Tab';
import Page from '../../components/Page';
import ApiUrls from '../../helper/ApiUrls';
import { API } from '../../services/axios.service';
import { hasGroup } from '../../helper';
import PlanUpgradeDialog from './inc/PlanUpgradeDialog';
import PlanUpgradeRequests from './inc/PlanUpgradeRequests';

export default function Settings() {
    const theme = useTheme();
    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const activeRootStyle = {
        color: theme.palette.primary.main,

        fontWeight: theme.typography.fontWeightMedium,
        bgcolor: alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity),
        '&:hover': {
            bgcolor: alpha(theme.palette.action.selected, theme.palette.action.hoverOpacity),
        },
        '&.Mui-selected': {
            color: theme.palette.text.primary,
        },
    };


    return (
        <Page title={'Settings'}>
            <Container >
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        height: 'auto',
                        backgroundColor: theme.palette.background.default
                    }}
                >
                    <Tabs
                        orientation="vertical"
                        value={value}
                        onChange={handleChange}
                        aria-label="Vertical tabs example"
                        centered="true"
                        sx={{ borderRight: 1, borderColor: 'divider' }}
                    >
                        {hasGroup("USER") && (
                            <Tab
                                label="Access Keys"
                                {...a11yProps(0)}
                                sx={
                                    value === 0
                                        ? activeRootStyle
                                        : {
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.action.selected, theme.palette.action.hoverOpacity),
                                            },
                                        }
                                }
                            />
                        )}
                        {hasGroup("USER") && (
                            <Tab
                                label="My Plan"
                                {...a11yProps(1)} // Fixed: should be index 1, not 0
                                sx={
                                    value === 1
                                        ? activeRootStyle
                                        : {
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.action.selected, theme.palette.action.hoverOpacity),
                                            },
                                        }
                                }
                            />
                        )}
                    </Tabs>
                    {hasGroup("USER") && <TabPanel style={{ flex: 1 }} value={value} index={0}>
                        <AccessKey />
                    </TabPanel>}
                    {hasGroup("USER") && <TabPanel style={{ flex: 1 }} value={value} index={1}>
                        <MyPlanSection />
                    </TabPanel>}
                </Box>
            </Container>
        </Page>
    );
}

const AccessKey = () => (
    <>
        <Box>
            <Grid container spacing={3}>
                <Grid item size={4}>
                    <Link style={{ textDecoration: 'none' }} to={'/dashboard/settings/access-key?access_origin=openai'}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '30px 40px' }}>

                            <Box sx={{ width: '80px', marginX: 'auto', paddingBottom: '20px' }}>
                                <img src={openAiImg} alt='open ai' />
                            </Box>
                            <Box sx={{
                                textAlign: 'center'
                            }}>
                                <Typography sx={{ fontWeight: '600', fontSize: '18px', textDecoration: 'none' }} variant="body2" color="text.secondary">
                                    OpenAI
                                </Typography>
                            </Box>

                        </Card>
                    </Link>
                </Grid>
                <Grid item size={4}>
                    <Link style={{ textDecoration: 'none' }} to={'/dashboard/settings/access-key?access_origin=bedrock'}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '30px 40px' }}>

                            <Box sx={{ width: '80px', marginX: 'auto', paddingBottom: '20px' }}>
                                <img src={bedrockImg} alt='open ai' />
                            </Box>
                            <Box sx={{
                                textAlign: 'center'
                            }}>
                                <Typography sx={{ fontWeight: '600', fontSize: '18px' }} variant="body2" color="text.secondary">
                                    AWS Bedrock
                                </Typography>
                            </Box>

                        </Card>
                    </Link>
                </Grid>
                <Grid item size={4}>
                    <Card
                        sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '30px 40px',
                            cursor: 'no-drop',
                            background: '',
                            position: 'relative',
                            justifyContent: 'center',
                            '&:hover .hoverBox': {
                                visibility: 'visible',
                                opacity: 1,
                            },
                            '&:hover': {
                                backdropFilter: 'blur(6px)', // blur on hover
                                backgroundColor: 'rgba(255, 255, 255, 0.8)', // slightly more solid bg
                            },
                        }}
                    >
                        <Box
                            className="hoverBox"
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                backgroundColor: "#fff",
                                padding: "10px 15px",
                                borderRadius: "32px",
                                boxShadow: "0 4px 12px 0 rgba(0, 0, 0, .078)",
                                visibility: "hidden",
                                opacity: 0,
                                width: "80%",
                                zIndex: 99,
                                textAlign: "center",
                                transition: "all .3s ease-in",
                                border: "1px solid #d1d5db"
                            }}
                        >
                            <p>Coming Soon</p>
                        </Box>

                        <Box sx={{ width: '80px', marginX: 'auto', paddingBottom: '20px' }}>
                            <img src={anthropicImg} alt='Anthropic' />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: '600', fontSize: '18px' }} variant="body2" color="text.secondary">
                                Anthropic
                            </Typography>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    </>
);

const MyPlanSection = () => {
    const [membershipData, setMembershipData] = useState(null);
    const [loading, setLoading] = useState(true);
    const user = useSelector(state => state.user.data);
    const [showPlanUpgradeDialog, setShowPlanUpgradeDialog] = useState(false);
    const [submittingPlanUpgradeRequest, setSubmittingPlanUpgradeRequest] = useState(false);
    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const pendingUpgradeExists = upgradeRequests?.some(req => req.status === 'PENDING');
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handlePlanUpgradeRequest = async (requestData) => {
        setSubmittingPlanUpgradeRequest(true);
        try {
            const response = await API.post(ApiUrls.MEMBERSHIP_REQUESTS_RESOURCE(user.id), requestData);
            console.log('Plan request submitted:', response.data);
            setUpgradeRequests(prev => [...prev, response.data.data]);
            
            // Close dialog after successful submission
            setShowPlanUpgradeDialog(false);
            enqueueSnackbar('Plan upgrade request submitted', {
                variant: "success",
            });
        } catch (error) {
            console.error('Failed to submit plan request:', error);
            enqueueSnackbar('Plan upgrade request failed', {
                variant: "warning",
            });
        } finally {
            setSubmittingPlanUpgradeRequest(false);
        }
    };

    const fetchMembershipRequestsData = async () => {
        setLoading(true);
        try {
            const response = await API.get(ApiUrls.MEMBERSHIP_REQUESTS_RESOURCE(user.id));
            setUpgradeRequests(response.data.data);
        } catch (error) {
            console.error('Error fetching membership requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [membershipRequestsResponse, membershipDataResponse] = await Promise.all([
                API.get(ApiUrls.MEMBERSHIP_REQUESTS_RESOURCE(user.id)),
                API.get(ApiUrls.USER_MEMBERSHIP(user.id))
            ]);

            setUpgradeRequests(membershipRequestsResponse.data.data);
            setMembershipData(membershipDataResponse.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const currentPlan = membershipData?.membership;
    const usage = membershipData?.log;

    const getUpgradeButtonText=()=>{
        if (submittingPlanUpgradeRequest) return 'Processing...';
        if (pendingUpgradeExists) return 'Plan Request Pending';
        return 'Choose Plan';
    }

    // No Active Membership Case
    if (!currentPlan) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    Membership Plan
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>No Active Membership</Typography>
                    <Typography sx={{ mb: 2 }}>
                        You don't have an active membership plan. Choose a plan to get started and unlock premium features!
                    </Typography>
                    <Button 
                        variant="outlined" 
                        sx={{ mt: 1 }}
                        onClick={() => setShowPlanUpgradeDialog(true)}
                        disabled={pendingUpgradeExists || submittingPlanUpgradeRequest}
                        startIcon={submittingPlanUpgradeRequest ? <CircularProgress size={20} /> : null}
                    >
                        {getUpgradeButtonText()}
                    </Button>
                </Alert>

                {/* Show pending requests if any */}
                {upgradeRequests.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Plan Requests
                        </Typography>
                        <PlanUpgradeRequests
                            upgradeRequests={upgradeRequests}
                            onRefresh={fetchMembershipRequestsData}
                        />
                    </Box>
                )}

                {/* Plan Selection Dialog */}
                <PlanUpgradeDialog
                    open={showPlanUpgradeDialog}
                    onClose={() => setShowPlanUpgradeDialog(false)}
                    onSubmitRequest={handlePlanUpgradeRequest}
                    membershipData={null} // Pass null for new memberships
                    loading={submittingPlanUpgradeRequest}
                    isNewMembership // Add flag to indicate this is a new membership
                />
            </Box>
        );
    }

    // Existing membership logic continues...
    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                Membership Plan
            </Typography>

            {/* Current Plan Summary Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item size={6}>
                            <Typography variant="h6" gutterBottom>
                                {currentPlan.name}
                            </Typography>
                            <Chip
                                label={membershipData.status}
                                color="success"
                                size="small"
                                sx={{ mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                Valid until: {new Date(membershipData.validity_till).toLocaleDateString()}
                            </Typography>
                        </Grid>
                        <Grid item size={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                            <Typography variant="h4" color="primary" gutterBottom>
                                ${currentPlan?.monthly_price || currentPlan?.one_time_price}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {currentPlan?.monthly_price ? 'per month' : 'one-time'}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Quick Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item size={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Token sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Token Usage</Typography>
                            </Box>
                            <Typography variant="h4" gutterBottom>
                                {(usage ? (usage?.consumed_input_tokens + usage?.consumed_output_tokens) : 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                of {currentPlan.token_limit?.toLocaleString() || '∞'} tokens
                            </Typography>
                            {usage && (
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(((usage.consumed_input_tokens + usage.consumed_output_tokens) / currentPlan.token_limit) * 100, 100)}
                                    sx={{ mt: 1, height: 6, borderRadius: 1 }}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item size={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Storage sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Storage Used</Typography>
                            </Box>
                            <Typography variant="h4" gutterBottom>
                                {usage?.consumed_knowledge_storage || '0'} GB
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                of {currentPlan.knowledge_size_limit || '∞'} GB
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                    Upgrade Requests
                </Typography>
                <PlanUpgradeRequests
                    upgradeRequests={upgradeRequests}
                    onRefresh={fetchMembershipRequestsData}
                />
            </Box>

            {/* Action Buttons */}
            <Card sx={{ mt: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Plan Management
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            startIcon={<AutorenewRounded />}
                            onClick={() => {
                                console.log('Extend plan');
                            }}
                            disabled
                        >
                            Extend Plan
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Upgrade />}
                            onClick={() => setShowPlanUpgradeDialog(true)}
                            disabled={pendingUpgradeExists}
                        >
                            {pendingUpgradeExists ? 'Upgrade Request Pending' : 'Upgrade Plan'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <PlanUpgradeDialog
                open={showPlanUpgradeDialog}
                onClose={() => setShowPlanUpgradeDialog(false)}
                onSubmitRequest={handlePlanUpgradeRequest}
                membershipData={membershipData}
                loading={submittingPlanUpgradeRequest}
                isNewMembership={false}
            />
        </Box>
    );
};